import type { CompSubscriptionPayload } from "@/hooks/useTeamSubscription"

export type UnbackedTeamLike = {
  team_public_id: string
  plan_id: string
  expires_at: string | null
}

/**
 * Build the comp request for a team that holds access with no billing row.
 *
 * The subtlety is `expiresAt`. The endpoint reads the KEY'S PRESENCE, not just
 * its value: an absent key means "the caller said nothing about the expiry" and
 * is treated as a retry of an existing comp, while an explicit `null` means "no
 * expiry". `JSON.stringify` drops `undefined` properties, so returning
 * `undefined` here would silently become an absent key on the wire — and a team
 * meant to get a perpetual comp would instead be handled as a repeat of terms it
 * does not have.
 *
 * Plan and expiry come straight from the team's existing access row: this makes
 * a grant real, it does not change its terms.
 */
export function buildCompPayload(
  team: UnbackedTeamLike,
  reason: string
): CompSubscriptionPayload {
  return {
    teamPublicId: team.team_public_id,
    planId: team.plan_id,
    reason: reason.trim(),
    expiresAt: team.expires_at ?? null,
  }
}

/**
 * Whether a team's existing expiry has already passed, which makes a comp for it
 * impossible rather than merely unusual.
 *
 * ## Why this survived #1463
 *
 * It was added as a workaround: `findTeamsWithUnbackedAccess` selected on
 * `team_subscriptions.status` alone and nothing enforced `expires_at`, so a
 * grant whose date went by last year was still `active`, still held access, and
 * still landed on this worklist — where `grantCompSubscription` would then
 * reject its past `expiresAt` with a 400. #1463 fixed that at the source: the
 * date is enforced now, and the worklist query drops expired rows, so a FRESH
 * response cannot contain one.
 *
 * What it still catches is the gap between fresh and rendered. This panel's
 * query does not poll and the admin QueryClient sets
 * `refetchOnWindowFocus: false`, so the page can sit open for hours holding rows
 * fetched when they were live — and a row expiring in that window is exactly the
 * shape this returns true for. Without the check, the stale row keeps offering
 * Comp; the click then finds the row absent from the refetch, and
 * `UnbackedAccessPanel`'s revalidation reports "a billing row backs its access
 * now", which is not what happened.
 *
 * So: no longer the enforcement, still the accurate message. Cheap, and it fails
 * in the safe direction if the backend filter is ever loosened again.
 *
 * Quietly substituting a future date is not the fix: this panel copies the team's
 * existing terms verbatim on purpose, and moving an expiry is a commercial
 * decision that has to be made deliberately, not as a side effect of a backfill.
 *
 * Mirrors the backend's own boundary (`parsed <= now` in `grantCompSubscription`,
 * `expires_at <= now` in `teamGrantHasExpired`). An unparseable date is NOT
 * treated as past — the backend has a distinct message for it, and guessing here
 * would report the wrong problem.
 */
export function compExpiryIsInThePast(expiresAt: string | null, now: number = Date.now()): boolean {
  if (!expiresAt) return false
  const parsed = Date.parse(expiresAt)
  if (!Number.isFinite(parsed)) return false
  return parsed <= now
}
